import Vue from 'vue'

import _ from 'lodash'

import { store } from '_/vuex/store'

import assetsManagerFactory from '_/vuex/modules/assetsManager'

const photonConfig = require('~/config/config.json')

import { eventBus } from '_/helpers/eventBus'

import { formatBytes } from '_/helpers/formatBytes'

import { mapGetters, mapActions } from 'vuex'

const FilePicker = Vue.component(
  'FilePicker',
  require('_/components/FieldTypes/AssetsManager/FilePicker/FilePicker.vue')
    .default
)

const AssetsManagerModal = Vue.component(
  'AssetsManagerModal',
  require('_/components/FieldTypes/AssetsManager/AssetsManagerModal/AssetsManagerModal.vue')
    .default
)

export default {
  /**
   * Define props
   *
   * @type  {Object}
   */
  props: {
    disabled: {
      type: Boolean,
    },
    id: {
      required: true,
      type: [Number, String],
    },
    multiple: {
      type: Boolean,
    },
    name: {
      required: true,
      type: String,
    },
    placeholder: {
      type: String,
    },
    refreshFields: {
      type: Number,
    },
    value: {
      default: null,
      type: [Array, Number, Object],
    },
    focus: {
      default: false,
      type: Boolean,
    },
  },

  /**
   * Set the data property
   *
   * @return  {object}
   */
  data() {
    return {
      /**
       * Toggles the visibility of clear all selected assets confirmation buttons
       *
       * @type  {boolean}
       */
      confirmClearAllSelectedAssets: false,

      /**
       * Selected assets, copied from asset manager modal after 'save & close' action
       *
       * @type  {Array}
       */
      selectedAssets: [],

      /**
       * Selected asset IDs, copied from asset manager modal after 'save & close' action
       *
       * @type  {Array}
       */
      selectedAssetsIds: [],

      /**
       * jQuery Sortable instance reference
       *
       * @type  {object}
       */
      sortable: null,
    }
  },

  /**
   * Set the computed properties
   *
   * @type  {object}
   */
  computed: {
    // Map getters
    ...mapGetters({
      admin: 'admin/admin',
    }),

    /**
     * Define the assetsManager getter
     *
     * @return  {object}
     */
    assetsManager() {
      const getterName = `${this.registeredModuleName}/${this.registeredModuleName}`

      return this.$store.getters[getterName]
    },

    /**
     * Gets the assets manager state
     *
     * @return  {boolean}
     */
    assetsManagerVisible() {
      return this.assetsManager.assetsManagerVisible
    },

    /**
     * Is the clear files button disabled
     *
     * @return  {boolean}
     */
    clearFilesButtonDisabled() {
      return this.disabled || _.isEmpty(this.selectedAssets)
    },

    /**
     * Gets the selected assets
     *
     * @return  {array}
     */
    getValue: function () {
      if (!this.multiple) {
        return this.selectedAssetsIds.length > 0
          ? this.createRelationshipPayload(this.selectedAssetsIds[0])
          : []
      }

      // In the new PCMS SAAS concept asset manager shouldn't create many=to-many relationships
      // so this shouldn't be a reachable part of the code
      return this.selectedAssetsIds
    },

    /**
     * Checks if there are selected asets
     *
     * @return  {Boolean}
     */
    hasSelectedAssets() {
      if (_.isEmpty(this.assetsManager.selectedAssets)) {
        return false
      }

      return true
    },

    /**
     * Gets the registeredModuleName
     *
     * @return  {string}
     */
    registeredModuleName() {
      return `assetsManager-${this.name}-${this.id}-${Date.now()}`
    },

    /**
     * Should the reorder info be shown or not
     *
     * @return  {boolean}
     */
    shouldShowReorderInfo() {
      return !_.isEmpty(this.selectedAssets) && this.selectedAssets.length > 1
    },
  },

  /**
   * Set the methods
   *
   * @type  {object}
   */
  methods: {
    /**
     * Creates the payload compatible with the relationship field type payload
     *
     * @param   {integer}  id
     * @return  {object}
     */
    createRelationshipPayload: function (id) {
      return {
        system: {
          id,
          module: {
            table_name: 'assets',
          },
        },
      }
    },

    /**
     * Resets the file input value
     *
     * @return  {void}
     */
    clearAllSelectedAssets() {
      this.setConfirmClearAllSelectedAssets(false)

      store.dispatch(`${this.registeredModuleName}/clearAllSelectedAssets`)

      this.selectedAssets = []

      this.selectedAssetsIds = []

      this.$emit('change', {
        id: this.id,
        name: this.name,
        value: this.getValue,
      })
    },

    /**
     * Formats byte to human readable value
     *
     * @param   {integer}  bytes
     * @return  {string}
     */
    formatBytes,

    /**
     * Checks mime type to determine if a file is an image
     *
     * @param   {string}  mimeType
     * @return  {Boolean}
     */
    isImage(mimeType) {
      return photonConfig.allowedImageMimeTypes.indexOf(mimeType) > -1
    },

    /**
     * Executes on close click (hides a modal)
     *
     * @return  {void}
     */
    closeAssetsManager() {
      store.dispatch(`${this.registeredModuleName}/assetsManagerVisible`, {
        value: false,
      })
    },

    /**
     * Initializes the asset manager
     *
     * @return  {promise}
     */
    initializeAssetManager() {
      return store
        .dispatch(`${this.registeredModuleName}/initializeState`, {
          multiple: this.multiple,
          value: this.value,
        })
        .then(() => {
          this.selectedAssetsIds = this.assetsManager.selectedAssetsIds

          return store
            .dispatch(`${this.registeredModuleName}/getSelectedAssets`, {
              selectedAssetsIds: this.assetsManager.selectedAssetsIds,
            })
            .then((response) => {
              this.selectedAssets = response
            })
        })
    },

    /**
     * Fired on assets selection
     *
     * @param   {object}  parameters
     * @return  {void}
     */
    onAssetSelection(parameters) {
      this.selectedAssets = this.assetsManager.selectedAssets

      this.selectedAssetsIds = this.assetsManager.selectedAssetsIds

      this.$emit('change', parameters)
    },

    /**
     * Shows modal window
     *
     * @return  {void}
     */
    openAssetsManager() {
      // this.initializeAssetManager();

      store.dispatch(`${this.registeredModuleName}/assetsManagerVisible`, {
        value: true,
      })
    },

    /**
     * Toggles the visibility of ClearAllSelectedAssets confirmation buttons
     *
     * @param  {boolean}  value
     * @return  {void}
     */
    setConfirmClearAllSelectedAssets(value) {
      this.confirmClearAllSelectedAssets = value
    },

    /**
     * Sort fields plugin setup
     *
     * @return  {void}
     */
    initializeSortable() {
      const $sortableFields = $(this.$el).find('.sortable-fields')

      if ($sortableFields.hasClass('ui-sortable')) {
        $sortableFields.sortable('destroy')
      }

      const updateFieldsOrder = this.updateFieldsOrder

      this.sortable = $sortableFields.sortable({
        axis: 'y',
        cancel: '.placeholder, .flip-it',
        containment: 'parent',
        cursor: 'move',
        items: '.sortable-item',
        opacity: 0.6,
        revert: false,
        scroll: true,
        scrollSensitivity: 50,
        tolerance: 'pointer',
        stop() {
          const newOrder = $sortableFields.sortable('toArray', {
            attribute: 'data-id',
          })

          updateFieldsOrder(newOrder)
        },
      })
    },

    /**
     * Updates the fields order after drag and drop action
     *
     * @param   {object}  newOrder
     * @return  {void}
     */
    updateFieldsOrder(newOrder) {
      this.$emit('change', {
        id: this.id,
        name: this.name,
        value: newOrder.map((id) => parseInt(id)),
      })

      // this.initializeAssetManager();
    },

    /**
     * Create asset manager Vuex module
     *
     * @return  {void}
     */
    createAssetManagerVuexModule() {
      const assetsManagerDynamicModuleName = this.registeredModuleName
      if (!_.has(store.state, assetsManagerDynamicModuleName)) {
        store.registerModule(
          assetsManagerDynamicModuleName,
          assetsManagerFactory(assetsManagerDynamicModuleName)
        )
      }
    },
  },

  /**
   * Sets a created hook
   *
   * @return  {void}
   */
  created: function () {
    this.createAssetManagerVuexModule()
  },

  /**
   * Set the mounted hook
   *
   * @return  {void}
   */
  mounted() {
    this.$nextTick(() => {
      this.initializeAssetManager()

      if (this.multiple) {
        this.initializeSortable()
      }

      if (this.focus) {
        $(this.$el).find('.btn-primary').focus()
      }
    })
  },

  watch: {
    refreshFields(newEntry, oldEntry) {
      if (newEntry !== oldEntry) {
        this.initializeAssetManager()

        if (this.multiple) {
          this.initializeSortable()
        }
      }
      if (this.focus) {
        $(this.$el).find('.btn-primary').focus()
      }
    },
    value: {
      deep: true,
      handler(newEntry, oldEntry) {
        this.initializeAssetManager()

        if (this.multiple) {
          // this.initializeSortable();
        }
      },
    },
  },
}
